import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA } from "@/components/CTA";
import { Tick } from "@/components/Annotation";
import { Tape, StickyNote } from "@/components/Notebook";
import { ShareCaseCard } from "@/components/ShareCaseCard";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

// ---- Static content (kept in English across all locales per brand rules) ----
const LOGOS = [
  { src: "/logos/amazon.png",  alt: "Amazon",                        w: "w-[150px] md:w-[190px]" },
  { src: "/logos/ert.png",     alt: "ΕΡΤ · Hellenic Broadcasting",   w: "w-[70px]  md:w-[90px]"  },
  { src: "/logos/avocado.png", alt: "Avocado Learning Experiences",  w: "w-[170px] md:w-[210px]" },
];

const FEEDBACK_PHRASES = [
  "Interaction and material discussed",
  "Great facilitator — very good work",
  "The facilitator was very engaging",
  "Interactive activities. Passion. Respect. Politeness.",
  "Great presentation and very helpful",
  "Engaging, to the point and resourceful under different circumstances",
  "Tips for grounding · the energy of the presenter · the actual examples",
  "Interactive — with real examples and scenarios",
  "The role play with the actor and the body exercises",
  "Interactive for most of its duration — a quality often missing from workshops",
  "The interactive exercises and the incorporation of physical practice",
];

const PARTICIPANT_QUOTES = [
  { q: "The facilitator was very engaging",                                                                        role: "Participant · Recruitment team" },
  { q: "Great presentation and very helpful",                                                                      role: "Participant · Recruitment team" },
  { q: "The practical part through the cases",                                                                     role: "Participant · Recruitment team" },
  { q: "Engaging, to the point and resourceful under different circumstances",                                     role: "Participant · Recruitment team" },
  { q: "Interactive activities. Passion. Respect. Politeness.",                                                     role: "Participant · Recruitment team" },
  { q: "The role play with the actor and the body exercises",                                                       role: "Participant · Recruitment team" },
  { q: "The workshop was interactive for most of its duration — a quality that is often missing from many workshops", role: "Participant · Recruitment team" },
  { q: "Tips for grounding · the energy of the presenter · the actual examples",                                    role: "Participant · Recruitment team" },
];

const CASE_STATS = [
  { value: "17",    label: "Participants" },
  { value: "100%",  label: "Feedback participation" },
  { value: "5.0/5", label: "Facilitator rating" },
  { value: "4.8/5", label: "Workshop rating" },
];

// ---- Reusable blocks -------------------------------------------------------
const LdTrust = ({ t }) => (
  <section className="relative z-10 py-14 md:py-20 border-y border-ink/10 bg-paper-deep/40" data-testid="ld-trust">
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-8 md:mb-10">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-ink-soft text-center">
        {t("ld.trust_label")}
      </p>
    </Reveal>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-12 md:gap-20 mb-10 md:mb-14">
      {LOGOS.map((l) => (
        <img key={l.alt} src={l.src} alt={l.alt} draggable={false}
          className={`${l.w} h-auto opacity-90 hover:opacity-100 transition-opacity duration-500 select-none`} />
      ))}
    </div>
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-5 md:mb-6">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta text-center">
        {t("ld.trust_marquee_label")}
      </p>
    </Reveal>
    <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
      <div className="flex w-max animate-marquee items-center gap-10 md:gap-14 whitespace-nowrap">
        {[...FEEDBACK_PHRASES, ...FEEDBACK_PHRASES].map((phrase, i) => (
          <span key={i} className="inline-flex items-center gap-10 md:gap-14">
            <span className="font-serif italic text-lg md:text-2xl text-ink/55 hover:text-ink transition-colors duration-500">
              &ldquo;{phrase}&rdquo;
            </span>
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta/60" />
          </span>
        ))}
      </div>
    </div>
  </section>
);

const RollingQuoteCards = () => (
  <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]" data-testid="ld-rolling-quotes">
    <div className="flex w-max animate-marquee items-stretch gap-6 md:gap-8">
      {[...PARTICIPANT_QUOTES, ...PARTICIPANT_QUOTES].map((c, i) => (
        <figure key={i} className="relative bg-paper border border-ink/12 p-7 md:p-8 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] w-[320px] md:w-[380px] shrink-0">
          <span aria-hidden="true" className="absolute -top-4 -left-1 font-serif text-6xl text-terracotta/50 leading-none">&ldquo;</span>
          <blockquote className="font-serif italic text-lg md:text-xl text-ink leading-snug">{c.q}</blockquote>
          <figcaption className="mt-4 font-hand text-pencil text-base">— {c.role}</figcaption>
        </figure>
      ))}
    </div>
  </div>
);

// ---- Page ------------------------------------------------------------------
export default function LDPartnerships() {
  const { t } = useLang();
  const roles = t("ld.roles_items") || [];
  const partners = t("ld.partners_items") || [];
  const brings = t("ld.brings_items") || [];

  return (
    <>
      <PageMeta
        titleKey="meta.ld_title"
        descriptionKey="meta.ld_description"
        path={ROUTES.ldPartnerships}
      />
      <PageHero
        testId="ld-hero"
        eyebrow={t("ld.hero_eyebrow")}
        headline={[t("ld.hero_h1_a"), t("ld.hero_h1_b"), t("ld.hero_h1_c")]}
        emphasis={t("ld.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("ld.hero_intro")}
        meta={<>{t("ld.hero_meta_line1")}<br />{t("ld.hero_meta_line2")}<br /><span className="text-mustard">{t("ld.hero_meta_line3")}</span></>}
      >
        <PrimaryCTA href={ROUTES.contact} testId="ld-primary-cta" location="hero" targetAssetId="contact_request">{t("ld.cta_discuss")}</PrimaryCTA>
        <OutlineCTA href={ROUTES.bookLdPartnership} testId="ld-book-cta" location="hero" targetAssetId="booking_ld_partnership">{t("ld.cta_intro")}</OutlineCTA>
      </PageHero>

      <LdTrust t={t} />

      {/* Belief — theatre → experiential learning */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ld-belief">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">{t("ld.belief_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.belief_h1")}<br /><span className="italic">{t("ld.belief_h2")}</span>
            </h2>
            <div className="mt-8 space-y-4 font-body text-lg text-ink-soft max-w-xl leading-relaxed">
              <p>{t("ld.belief_p1")}</p>
              <p>{t("ld.belief_p2")}</p>
              <p>{t("ld.belief_p3")}</p>
              <p className="font-body text-ink-soft leading-relaxed">{t("ld.hero_intro2")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative bg-paper border border-ink/12 p-8 md:p-10 shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
              <Tape className="absolute -top-4 left-8" rotate={-4} width={110} />
              <p className="font-body text-sm uppercase tracking-[0.22em] text-terracotta mb-6">{t("ld.journey_label")}</p>
              <p className="font-serif text-2xl md:text-3xl italic text-ink leading-tight">
                {t("ld.journey_arrow")}
              </p>
              <p className="mt-6 font-body text-ink-soft leading-relaxed">
                {t("ld.journey_body")}
              </p>
              <div className="mt-8">
                <StickyNote rotate={-2} color="#F2EBDA">{t("ld.journey_note")}</StickyNote>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Visual — Theatre → Role-play → Experiential Learning */}
      <section className="relative z-10 py-16 md:py-24 bg-paper-deep/50 border-y border-ink/10" data-testid="ld-visuals">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Reveal className="mb-10 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-4">
              {t("ld.visual_eyebrow")}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink">
              {t("ld.visual_h1")}
            </h2>
            <p className="mt-6 font-body text-ink-soft leading-relaxed max-w-2xl">
              {t("ld.visual_body")}
            </p>
            <p className="mt-4 font-hand text-terracotta text-xl md:text-2xl">
              {t("ld.visual_hand")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <figure className="relative max-w-[900px] mx-auto">
              <Tape className="absolute -top-4 left-10 z-10" rotate={-4} width={100} />
              <Tape className="absolute -top-4 right-10 z-10" rotate={4} width={100} />
              <div className="bg-paper border border-ink/12 shadow-[6px_12px_36px_rgba(27,27,24,0.10)] p-3 md:p-5">
                <img
                  src="/sketches/ld-theatre-journey.webp"
                  loading="lazy"
                  alt={t("ld.theatre_journey_alt")}
                  className="w-full h-auto block cursor-zoom-in"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="ld-theatre-journey-image"
                />
              </div>
              <figcaption className="mt-4 font-hand text-terracotta text-center text-lg md:text-xl">
                {t("ld.visual_caption_a")}
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={0.2} className="mt-16">
            <figure className="relative max-w-[820px] mx-auto">
              <Tape className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" rotate={-3} width={110} />
              <div className="bg-paper border border-ink/12 shadow-[6px_12px_36px_rgba(27,27,24,0.10)] p-3 md:p-5">
                <img
                  src="/sketches/ld-try-observe-apply.webp"
                  loading="lazy"
                  alt={t("ld.try_observe_apply_alt")}
                  className="w-full h-auto block cursor-zoom-in"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="ld-try-observe-apply-image"
                />
              </div>
              <figcaption className="mt-4 font-hand text-terracotta text-center text-lg md:text-xl">
                {t("ld.visual_caption_b")}
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* How we can work together */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ld-roles">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("ld.roles_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.roles_h1")}<br /><span className="italic">{t("ld.roles_h2")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-2 gap-6 md:gap-8">
            {roles.map((r, i) => (
              <StaggerItem key={r.t}>
                <div className="relative bg-paper border border-ink/12 p-8 h-full shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
                  <span className="font-hand text-terracotta text-2xl leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-tight">{r.t}</h3>
                  <p className="mt-4 font-body text-ink-soft leading-relaxed">{r.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <p className="mt-10 font-hand text-terracotta text-2xl md:text-3xl max-w-2xl">
            {t("ld.roles_note")}
          </p>
        </div>
      </section>

      {/* Who we partner with */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="ld-partners">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("ld.partners_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.partners_h1")}<br /><span className="italic">{t("ld.partners_h2")}</span>
            </h2>
            <p className="mt-8 font-body text-ink-soft leading-relaxed max-w-md">
              {t("ld.partners_body")}
            </p>
            <p className="mt-6 font-hand text-terracotta text-xl">
              {t("ld.partners_note")}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta mb-5">{t("ld.partners_list_label")}</p>
            <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-8">
              {partners.map((p) => (
                <li key={p} className="flex items-start gap-3 border-t border-ink/15 pt-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* What I bring */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ld-brings">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("ld.brings_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.brings_h1")}<br /><span className="italic">{t("ld.brings_h2")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {brings.map((b, i) => (
              <StaggerItem key={b.t}>
                <div className="border-t border-ink/25 pt-6">
                  <span className="font-hand text-terracotta text-lg leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-2 font-serif text-xl md:text-2xl text-ink">{b.t}</h3>
                  <p className="mt-3 font-body text-ink-soft leading-relaxed">{b.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* A partnership already in practice — Avocado */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="ld-avocado">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("ld.avocado_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.avocado_h1")}<br /><span className="italic">{t("ld.avocado_h2")}</span>
            </h2>
          </Reveal>

          <article className="grid lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-5">
              <img
                src="/logos/avocado.png"
                alt="Avocado Learning Experiences"
                className="w-[180px] md:w-[220px] h-auto mb-6 opacity-95"
                draggable={false}
              />
              <p className="font-body text-ink-soft leading-relaxed">
                {t("ld.avocado_intro")}
              </p>

              <div className="mt-8 relative bg-paper border border-ink/12 p-7 shadow-[4px_8px_24px_rgba(27,27,24,0.06)]">
                <Tape className="absolute -top-4 left-6" rotate={-4} width={90} />
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("ld.avocado_elements_label")}
                </p>
                <p className="font-serif text-2xl md:text-3xl italic text-ink leading-snug">
                  {t("ld.avocado_elements")}
                </p>
              </div>

              <figure className="mt-8 relative">
                <Tape className="absolute -top-3 left-6 z-10" rotate={-3} width={80} />
                <div className="bg-paper border border-ink/12 shadow-[4px_8px_24px_rgba(27,27,24,0.08)] p-3">
                  <img
                    src="/sketches/ld-collaboration.webp"
                    loading="lazy"
                    alt={t("ld.collaboration_alt")}
                    className="w-full h-auto block cursor-zoom-in"
                    style={{ mixBlendMode: "multiply" }}
                    draggable={false}
                    data-testid="ld-collab-image"
                  />
                </div>
              </figure>
            </div>

            <div className="lg:col-span-7">
              <p className="font-body text-ink-soft leading-relaxed mb-6">
                {t("ld.avocado_body1")}
              </p>
              <p className="font-body text-ink-soft leading-relaxed mb-8">
                {t("ld.avocado_body2")}
              </p>

              <div className="bg-paper border border-ink/12 p-6 mb-6">
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("ld.avocado_valued_label")}
                </p>
                <p className="font-body text-ink leading-relaxed">
                  {t("ld.avocado_valued")}
                </p>
              </div>

              <blockquote className="mt-6 border-l-2 border-terracotta pl-5 font-serif italic text-2xl md:text-3xl text-ink leading-snug">
                {t("ld.avocado_takeaway")}
              </blockquote>
              <p className="mt-6 font-hand text-terracotta text-lg md:text-xl leading-snug max-w-md">
                {t("ld.avocado_partnership_note")}
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Mentimeter ratings + rolling participant quote cards */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ld-workshop">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-14">
          <Reveal className="max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("ld.workshop_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.workshop_h1")}<br /><span className="italic">{t("ld.workshop_h2")}</span>
            </h2>
          </Reveal>
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-14">
          <Reveal>
            <div className="relative bg-ink text-paper px-6 md:px-12 py-12 md:py-14">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-8 text-center">
                {t("ld.workshop_ratings_label")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
                {CASE_STATS.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-serif text-5xl md:text-6xl lg:text-7xl text-mustard leading-none">{s.value}</p>
                    <p className="mt-3 font-body text-xs md:text-sm uppercase tracking-[0.2em] text-paper/80">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal>
          <RollingQuoteCards />
        </Reveal>
      </section>

      {/* Flexibility */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="ld-flexibility">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("ld.flex_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("ld.flex_h1")}<br /><span className="italic">{t("ld.flex_h2")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 font-body text-lg text-ink-soft leading-relaxed">
            <p>{t("ld.flex_p1")}</p>
            <p>
              {t("ld.flex_p2_lead")}
              <strong className="text-ink font-normal">{t("ld.flex_p2_langs")}</strong>
              {t("ld.flex_p2_tail")}
            </p>
            <p>{t("ld.flex_p3")}</p>
          </Reveal>
        </div>
      </section>

      <ShareCaseCard programmeTitle="L&D Partnerships · InterAct Globally" testId="ld-share" />

      {/* Final CTA */}
      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="ld-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">{t("ld.final_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-paper">
              {t("ld.final_h1")}<br /><span className="italic">{t("ld.final_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto leading-relaxed">
              {t("ld.final_body")}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.contact} testId="ld-final-contact-cta" location="final_cta" targetAssetId="contact_request">{t("ld.final_cta_start")}</PrimaryCTA>
            <OutlineCTA
              href={ROUTES.bookLdPartnership}
              className="!border-paper/75 !text-paper hover:!border-mustard hover:!text-mustard"
              testId="ld-final-book-cta"
              location="final_cta"
              targetAssetId="booking_ld_partnership"
            >
              {t("ld.final_cta_intro")}
            </OutlineCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
